import React from 'react';
import { Header } from '../../components/layout/Header';
import { PageHeader, UrlTabs, useUrlTab } from '../../components/ui';
import { useAuth } from '../../lib/auth/AuthContext';
import { useInsightsFilters } from '../../lib/hooks/useInsightsFilters';
import { InsightsFilterBar } from '../../components/insights/InsightsFilterBar';
import { InsightsOverviewPanel } from '../../components/insights/panels/InsightsOverviewPanel';
import { SpendPanel } from '../../components/insights/panels/SpendPanel';
import { EfficiencyPanel } from '../../components/insights/panels/EfficiencyPanel';
import { HealthReliabilityPanel } from '../../components/insights/panels/HealthReliabilityPanel';

type TabId = 'overview' | 'spend' | 'efficiency' | 'reliability';

const TABS: ReadonlyArray<{ id: TabId; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'spend', label: 'Spend' },
  { id: 'efficiency', label: 'Efficiency' },
  { id: 'reliability', label: 'Reliability' },
];
const TAB_IDS = TABS.map((t) => t.id);

/**
 * UX-02 — /insights, the hub for what was four separate report pages
 * (Health, Spend, Efficiency, Insights). One shared range + project filter
 * bar drives all 4 tabs via `useInsightsFilters()`; /health, /reports/spend
 * and /analytics/cost-efficiency now redirect here (see App.tsx +
 * lib/insightsRedirects.ts).
 */
export const InsightsHubPage: React.FC = () => {
  const { currentAccount } = useAuth();
  const account = currentAccount?.id ?? undefined;
  const { since, project, setSince, setProject } = useInsightsFilters();
  const [activeTab] = useUrlTab('tab', TAB_IDS, 'overview');

  return (
    <div data-testid="insights-hub-page" className="min-h-screen bg-slate-50">
      <Header title="Insights" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <PageHeader
          title="Insights"
          description="How your workspace is doing — spend, efficiency, and reliability in one place."
          actions={
            <InsightsFilterBar
              since={since}
              setSince={setSince}
              project={project}
              setProject={setProject}
            />
          }
        />

        <UrlTabs param="tab" tabs={TABS} defaultId="overview" testIdPrefix="tab" />

        {activeTab === 'overview' && (
          <InsightsOverviewPanel since={since} project={project} account={account} />
        )}
        {activeTab === 'spend' && (
          <SpendPanel since={since} project={project} setProject={setProject} account={account} />
        )}
        {activeTab === 'efficiency' && (
          <EfficiencyPanel since={since} project={project} setProject={setProject} account={account} />
        )}
        {activeTab === 'reliability' && (
          <HealthReliabilityPanel since={since} project={project} setProject={setProject} account={account} />
        )}
      </main>
    </div>
  );
};

export default InsightsHubPage;
